import { useState, useEffect } from 'react';
import {
  ComposedChart,
  Line,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine
} from 'recharts';

export default function UmsatzChart({ daten }) {
  const [colors, setColors] = useState({
    primary: '#378ab2',
    warning: '#f59e0b',
    muted: '#94a3b8'
  });

  useEffect(() => {
    const style = getComputedStyle(document.documentElement);
    setColors({
      primary: style.getPropertyValue('--primary').trim() || '#378ab2',
      warning: style.getPropertyValue('--warning').trim() || '#f59e0b',
      muted: '#94a3b8'
    });
  }, []);

  if (!daten || !daten.aktuell || daten.aktuell.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        Keine Daten verfügbar
      </div>
    );
  }

  const chartData = daten.aktuell.map((d, index) => ({
    monat: d.monat,
    bezahlt: d.bezahlt,
    offen: d.offen,
    vorjahrBezahlt: daten.vorjahr[index]?.bezahlt || 0
  }));

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const filteredPayload = payload.filter(p => p.name !== 'bezahltFill');
      return (
        <div className="bg-white/95 backdrop-blur-sm rounded-lg shadow-sm border border-gray-100 px-3 py-2">
          <p className="text-xs font-medium text-gray-900 mb-1.5">{label}</p>
          {filteredPayload.map((entry, index) => (
            <div key={index} className="flex items-center justify-between gap-4 text-xs">
              <span className="text-gray-500">{entry.name}</span>
              <span className="font-medium" style={{ color: entry.stroke || entry.color }}>
                {entry.value.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} €
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  const axisStyle = {
    fontSize: '11px',
    fontFamily: 'var(--heading-font), ui-sans-serif, system-ui, sans-serif',
    fill: '#94a3b8'
  };

  return (
    <ResponsiveContainer width="100%" height={350}>
      <ComposedChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id="gradientBezahlt" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={colors.primary} stopOpacity={0.08} />
            <stop offset="100%" stopColor={colors.primary} stopOpacity={0} />
          </linearGradient>
        </defs>

        <CartesianGrid
          strokeDasharray="3 3"
          stroke="#e2e8f0"
          strokeOpacity={0.4}
          vertical={false}
        />

        <ReferenceLine y={0} stroke="#e2e8f0" strokeWidth={1} />

        <XAxis
          dataKey="monat"
          axisLine={false}
          tickLine={false}
          tick={axisStyle}
          dy={8}
        />
        <YAxis
          axisLine={false}
          tickLine={false}
          tick={axisStyle}
          tickFormatter={(value) => value >= 1000 ? `${(value / 1000).toLocaleString('de-DE')}k` : value}
          width={45}
        />

        <Tooltip
          content={<CustomTooltip />}
          cursor={{ stroke: '#e2e8f0', strokeWidth: 1 }}
        />

        <Area
          type="monotoneX"
          dataKey="bezahlt"
          stroke="none"
          fill="url(#gradientBezahlt)"
          name="bezahltFill"
        />

        <Line
          type="monotoneX"
          dataKey="bezahlt"
          stroke={colors.primary}
          strokeWidth={2}
          name="Bezahlt"
          dot={false}
          activeDot={{ r: 4, fill: colors.primary, strokeWidth: 0 }}
        />
        <Line
          type="monotoneX"
          dataKey="offen"
          stroke={colors.warning}
          strokeWidth={2}
          name="Offen"
          dot={false}
          activeDot={{ r: 4, fill: colors.warning, strokeWidth: 0 }}
        />
        <Line
          type="monotoneX"
          dataKey="vorjahrBezahlt"
          stroke={colors.muted}
          strokeWidth={1.5}
          strokeDasharray="4 4"
          name="Vorjahr"
          dot={false}
          activeDot={{ r: 3, fill: colors.muted, strokeWidth: 0 }}
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
}
